import TaskProgress from "../TaskProgress/TaskProgress";
import {
  addDiscoveredTask,
  clearAllTasks,
  clearSelectedTasks,
  clearTask,
} from "../../../store/slice/notificationSlice";
import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { isAuthenticated as checkAuth } from "../../../store/slice/authSlice";
import {
  useGetActiveTasksQuery,
  useDismissTasksMutation,
} from "../../../store/apiSlice/notificationApiSlice";
import { apiSlice } from "../../../store/apiSlice/apiSlice";
import { useSelector, useDispatch } from "react-redux";
import { DataClearLock } from "../../../app/organization/InboundClearLock";
import {
  DATA_CLEAR_STORAGE_KEY,
  DATA_CLEAR_START_GRACE_MS,
  dataClearMarkedAtMs,
  dataClearStorageKey,
  isDataClearActivelyMarked,
  isHiddenFromTaskManager,
  persistDataClearIdle,
  persistDataClearRunning,
  releaseStaleDataClearMark,
} from "../../../app/organization/dataClearConstants";
import { REFRESH_PROJECTIONS_TASK_TITLE } from "../../../app/planner/constants";
import { setTaskNotificationsMuted as persistTaskNotificationsMuted } from "../TaskProgress/taskNotificationsMute";
import { setTaskNotificationsMuted } from "../../../store/slice/miscSlice";

/**
 * Context for managing background tasks and progress monitoring.
 */
const TaskContext = createContext(null);

/** Ref callback target: TopBar right cluster before NotificationBell. */
const TaskProgressMountContext = createContext(null);

export const useTaskProgressMountRef = () => useContext(TaskProgressMountContext);

/**
 * Hook to access the task monitoring context.
 *
 * @returns {object} Context object containing methods to manage and monitor tasks.
 * @throws {Error} If used outside of a TaskProvider.
 */
export const useTaskMonitor = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error("useTaskMonitor must be used within a TaskProvider");
  }
  return context;
};

const companyIdOf = user => user?.company_id ?? user?.company?.id;

/**
 * Provider component that orchestrates background task monitoring.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 */
export const TaskProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [topbarMountEl, setTopbarMountEl] = useState(null);
  const setTopbarMountRef = useCallback(node => {
    setTopbarMountEl(prev => {
      if (node) {
        return node;
      }
      // If incoming node is null, only reset if the previous element is no longer attached to DOM.
      // This prevents race conditions where an unmounting TopBar wipes out a newly mounted TopBar.
      if (prev && typeof document !== "undefined" && !document.body.contains(prev)) {
        setIsPanelOpen(false);
        return null;
      }
      return prev;
    });
  }, []);
  const user = useSelector(state => state.auth.user);
  const companyId = companyIdOf(user);
  const [clearMarked, setClearMarked] = useState(() => isDataClearActivelyMarked(companyId));
  const bootstrappedRef = useRef(false);
  /** @type {React.MutableRefObject<Set<string>>} */
  const completedRefreshIdsRef = useRef(new Set());

  const tasksMap = useSelector(state => state.notification?.tasks || {});
  const taskNotificationsMuted = useSelector(
    state => Boolean(state.misc?.taskNotificationsMuted),
  );
  const clearJob = useSelector(
    state => apiSlice.endpoints.getDataResetCurrent.select(undefined)(state)?.data,
  );
  const runningClearTaskId =
    clearJob?.status === "running" && clearJob?.task_id ? clearJob.task_id : "";
  // Do not use the post-clear hold here — it used to leave clearMarked stuck true.
  const clearInProgress =
    clearMarked || clearJob?.status === "running" || Boolean(runningClearTaskId);

  const taskIds = Object.keys(tasksMap).filter(
    id => !isHiddenFromTaskManager(tasksMap[id], runningClearTaskId),
  );

  const isAuthenticated = useSelector(checkAuth);
  const [dismissTasks] = useDismissTasksMutation();

  const { data: activeTasks, isSuccess } = useGetActiveTasksQuery(undefined, {
    skip: !isAuthenticated,
  });

  useEffect(() => {
    setClearMarked(isDataClearActivelyMarked(companyId));
  }, [companyId]);

  // Server says idle but storage still "running" → Task Manager stays gone.
  // Re-check after start grace so Confirm→POST does not flash the button back.
  useEffect(() => {
    if (clearJob === undefined) return undefined;
    if (clearJob?.status === "running") {
      setClearMarked(true);
      return undefined;
    }
    const status = clearJob?.status ?? null;
    if (releaseStaleDataClearMark(companyId, status)) {
      setClearMarked(false);
      window.dispatchEvent(new Event("data-clear-changed"));
      return undefined;
    }
    setClearMarked(isDataClearActivelyMarked(companyId));
    const markedAt = dataClearMarkedAtMs(companyId);
    if (!markedAt) return undefined;
    const remaining = DATA_CLEAR_START_GRACE_MS - (Date.now() - markedAt);
    if (remaining <= 0) return undefined;
    const timer = window.setTimeout(() => {
      if (releaseStaleDataClearMark(companyId, status)) {
        setClearMarked(false);
        window.dispatchEvent(new Event("data-clear-changed"));
      } else {
        setClearMarked(isDataClearActivelyMarked(companyId));
      }
    }, remaining + 50);
    return () => window.clearTimeout(timer);
  }, [clearJob, companyId]);

  useEffect(() => {
    const sync = () => setClearMarked(isDataClearActivelyMarked(companyId));
    const storageKey = dataClearStorageKey(companyId);
    const onStorage = event => {
      if (event.key !== storageKey && event.key !== DATA_CLEAR_STORAGE_KEY) return;
      if (event.newValue && String(event.newValue).startsWith("running")) {
        persistDataClearRunning(companyId);
      } else {
        persistDataClearIdle(companyId);
      }
      setIsPanelOpen(false);
      sync();
    };
    window.addEventListener("data-clear-changed", sync);
    window.addEventListener("inbound-clear-changed", sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("data-clear-changed", sync);
      window.removeEventListener("inbound-clear-changed", sync);
      window.removeEventListener("storage", onStorage);
    };
  }, [companyId]);

  useEffect(() => {
    if (clearInProgress) {
      setIsPanelOpen(false);
    }
  }, [clearInProgress]);

  // After Refreshing Requirements finishes, refetch planner lists once per task.
  useEffect(() => {
    Object.entries(tasksMap).forEach(([taskId, task]) => {
      if (task?.status !== "COMPLETED") {
        return;
      }
      const title = task?.data?.title || task?.title;
      if (title !== REFRESH_PROJECTIONS_TASK_TITLE) {
        return;
      }
      if (completedRefreshIdsRef.current.has(taskId)) {
        return;
      }
      completedRefreshIdsRef.current.add(taskId);
      dispatch(apiSlice.util.invalidateTags(["projectedStockList", "plannedOrderList"]));
    });
  }, [tasksMap, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const key = "discovery-stream";
    dispatch({
      type: "fileSSE/start",
      payload: {
        category: "discovery",
        events: ["discovery"],
        key,
        url: "/sse_ntf/discovery/stream/",
      },
    });

    return () => {
      dispatch({ type: "fileSSE/stop", payload: { key } });
    };
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isSuccess || !Array.isArray(activeTasks)) return;

    const panelTaskIds = activeTasks.filter(id => id !== runningClearTaskId);
    panelTaskIds.forEach(id => {
      dispatch(addDiscoveredTask({ task_id: id }));
    });

    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      if (panelTaskIds.length > 0 && !clearInProgress && !taskNotificationsMuted) {
        setIsPanelOpen(true);
      }
    }
  }, [
    activeTasks,
    isSuccess,
    dispatch,
    runningClearTaskId,
    clearInProgress,
    taskNotificationsMuted,
  ]);

  const prevCountRef = useRef(0);
  useEffect(() => {
    const currentCount = taskIds.length;
    if (clearInProgress || isDataClearActivelyMarked(companyId)) {
      prevCountRef.current = currentCount;
      setIsPanelOpen(false);
      return undefined;
    }
    if (
      currentCount > prevCountRef.current &&
      !isPanelOpen &&
      !taskNotificationsMuted
    ) {
      const timer = setTimeout(() => {
        if (isDataClearActivelyMarked(companyId) || taskNotificationsMuted) {
          setIsPanelOpen(false);
          return;
        }
        // Only auto-open if a valid TopBar mount is currently active in the DOM
        if (
          topbarMountEl &&
          typeof document !== "undefined" &&
          document.body.contains(topbarMountEl)
        ) {
          setIsPanelOpen(true);
        }
      }, 100);
      prevCountRef.current = currentCount;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = currentCount;
    return undefined;
  }, [
    taskIds.length,
    isPanelOpen,
    clearInProgress,
    companyId,
    topbarMountEl,
    taskNotificationsMuted,
  ]);

  useEffect(() => {
    if (isAuthenticated === false) {
      bootstrappedRef.current = false;
      dispatch(clearAllTasks());
    }
  }, [isAuthenticated, dispatch]);

  const startMonitoring = useCallback(
    taskId => {
      if (!taskId || taskId === runningClearTaskId || clearInProgress) return;
      dispatch(addDiscoveredTask({ task_id: taskId }));
      if (!taskNotificationsMuted) {
        setIsPanelOpen(true);
      }
    },
    [clearInProgress, dispatch, runningClearTaskId, taskNotificationsMuted],
  );

  const toggleTaskNotificationsMute = useCallback(() => {
    const next = !taskNotificationsMuted;
    persistTaskNotificationsMuted(next);
    dispatch(setTaskNotificationsMuted(next));
  }, [dispatch, taskNotificationsMuted]);

  const removeTask = useCallback(
    taskId => {
      dispatch(clearTask(taskId));
      dismissTasks([taskId]);
    },
    [dispatch, dismissTasks],
  );

  const clearTasks = useCallback(
    type => {
      const finishedIds = taskIds.filter(id => {
        const status = tasksMap[id]?.status;
        return status === "COMPLETED" || status === "FAILED";
      });

      const successfulIds = taskIds.filter(id => tasksMap[id]?.status === "COMPLETED");

      const idsToClear = type === "all" ? finishedIds : successfulIds;

      if (idsToClear.length > 0) {
        dismissTasks(idsToClear);
        dispatch(clearSelectedTasks(idsToClear));
      }
      return idsToClear.length;
    },
    [taskIds, tasksMap, dismissTasks, dispatch],
  );

  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const isMountValid =
    Boolean(topbarMountEl) &&
    typeof document !== "undefined" &&
    document.body.contains(topbarMountEl);

  const taskProgressUi =
    isAuthenticated && isMountValid ? (
      <TaskProgress
        hidden={clearInProgress}
        onAddTask={startMonitoring}
        onClearTasks={clearTasks}
        onClose={closePanel}
        onRemoveTask={removeTask}
        onToggle={togglePanel}
        onToggleTaskNotificationsMute={toggleTaskNotificationsMute}
        taskIds={taskIds}
        taskNotificationsMuted={taskNotificationsMuted}
        visible={isPanelOpen && !clearInProgress}
      />
    ) : null;

  return (
    <TaskContext.Provider value={{ clearTasks, removeTask, startMonitoring, taskIds, togglePanel }}>
      <TaskProgressMountContext.Provider value={setTopbarMountRef}>
        {children}
        {isAuthenticated && !window.location.pathname.startsWith("/portal") && <DataClearLock />}
        {taskProgressUi && createPortal(taskProgressUi, topbarMountEl)}
      </TaskProgressMountContext.Provider>
    </TaskContext.Provider>
  );
};
